/**
 * FERP Portal - Agent Assist Panel (DRR-187)
 *
 * Generic AI agent interaction panel (chat + action cards).
 * Backend-driven: the agent determines what entities to create,
 * what workflows to launch, and what fields to pre-fill.
 * Frontend only renders proposals and captures HITL confirmation.
 *
 * Pipeline: Device OCR → Presidio NER (hub) → tokenized text → Claude → proposals
 * The agent proposes, the human confirms. No auto-writes.
 *
 * Domain-agnostic: works for healthcare intake, financial onboarding,
 * legal enrollment — the backend agent handles context.
 *
 * FERP Principles:
 * - Dumb Frontend: Agent drives the flow, frontend only renders proposals
 * - FormSchema IS the mapping contract: extracted fields map to backend schema
 * - HITL: Every entity creation requires explicit user confirmation
 * - DRR-114: T4 media processed on-device, never uploaded
 * - DRR-128: Write-time minimization — only structured text leaves device
 */

import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Title,
  Text,
  Group,
  Stack,
  ActionIcon,
  Button,
  Drawer,
  ScrollArea,
  Badge,
  Alert,
  Card,
  Textarea,
  Tooltip,
  Divider,
  Loader,
  ThemeIcon,
} from '@mantine/core';
import {
  IconCamera,
  IconCheck,
  IconEdit,
  IconPlayerSkipForward,
  IconMessageChatbot,
  IconAlertTriangle,
  IconUser,
  IconHeartbeat,
  IconPlayerPlay,
  IconSend,
} from '@tabler/icons-react';
import { gql } from '@apollo/client/core';
import { useMutation } from '@apollo/client/react';
import { useNavigate } from 'react-router-dom';
import { useScanToFill } from '@ferp/workflow-forms';
import { useIntakeProcess, type IntakeProposal, type IntakeStep } from '@/hooks/useIntakeProcess';
import { usePrimitiveMutations } from '@/hooks/usePrimitiveMutations';
import { PrimitiveFormModal } from '@/components/primitives/PrimitiveFormModal';
import { useTenantId } from '@/auth';
import { useStartInstanceMutation } from '@/generated/graphql';
import type { PrimitiveType } from '@/config/primitive-metadata';

// DRR-182: Draft instance mutations (reused from WorkflowLaunchModal pattern)
const CREATE_DRAFT_INSTANCE = gql`
  mutation CreateDraftInstance($input: GqlCreateInstanceInput!) {
    createDraftInstance(input: $input) {
      draftId existingId created message
    }
  }
`;
const COMMIT_DRAFT_INSTANCE = gql`
  mutation CommitDraftInstance($draftId: String!) {
    commitDraftInstance(draftId: $draftId) { id status currentState instanceName }
  }
`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AgentAssistPanelProps {
  /** Whether the drawer is open */
  opened: boolean;
  /** Close the panel */
  onClose: () => void;
  /** Callback when an entity is created (for parent refresh) */
  onEntityCreated?: (entityType: string, entityId: string) => void;
  /** Callback when a workflow is launched */
  onWorkflowLaunched?: (workflowId: string, instanceId: string) => void;
}

interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  proposals?: IntakeProposal[];
  suggestedNextSteps?: string[];
}

// ---------------------------------------------------------------------------
// Proposal icon resolver (backend-driven proposal types)
// ---------------------------------------------------------------------------

const PROPOSAL_ICON_MAP: Record<string, typeof IconUser> = {
  create_actor: IconUser,
  create_fhir_patient: IconHeartbeat,
  launch_workflow: IconPlayerPlay,
};

function getProposalIcon(proposalType: string) {
  return PROPOSAL_ICON_MAP[proposalType] ?? IconCheck;
}

function getProposalLabel(proposalType: string, target: string): string {
  // Labels are generic — the agent provides the real context via `reason`
  if (proposalType.startsWith('create_')) {
    const entityName = target || proposalType.replace('create_', '');
    return `Create ${entityName}`;
  }
  if (proposalType === 'launch_workflow') return 'Launch Workflow';
  return proposalType;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Renders a single agent proposal as an action card with confirm/edit/skip */
function ProposalCard({
  proposal,
  onConfirm,
  onEdit,
  onSkip,
}: {
  proposal: IntakeProposal;
  onConfirm: (proposal: IntakeProposal) => void;
  onEdit: (proposal: IntakeProposal) => void;
  onSkip: (proposal: IntakeProposal) => void;
}) {
  const Icon = getProposalIcon(proposal.proposalType);
  const label = getProposalLabel(proposal.proposalType, proposal.target);
  const color = proposal.proposalType === 'launch_workflow' ? 'blue' : 'teal';

  return (
    <Card withBorder shadow="xs" p="sm" radius="md">
      <Group justify="space-between" mb="xs">
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color={color}>
            <Icon size={14} />
          </ThemeIcon>
          <Text fw={600} size="sm">{label}</Text>
        </Group>
        <Badge
          size="xs"
          color={proposal.confidence >= 0.7 ? 'green' : proposal.confidence >= 0.4 ? 'yellow' : 'red'}
        >
          {Math.round(proposal.confidence * 100)}%
        </Badge>
      </Group>

      <Text size="xs" c="dimmed" mb="xs">{proposal.reason}</Text>

      {/* Extracted fields preview */}
      {proposal.extractedFields.length > 0 && (
        <Stack gap={4} mb="sm">
          {proposal.extractedFields.slice(0, 5).map((field) => (
            <Group key={field.fieldName} gap="xs">
              <Text size="xs" c="dimmed" w={100}>{field.fieldName}:</Text>
              <Text
                size="xs"
                fw={500}
                c={field.confidence < 0.7 ? 'yellow.7' : undefined}
              >
                {String(field.value)}
              </Text>
              {field.confidence < 0.7 && (
                <Tooltip label="Low confidence — verify this field">
                  <IconAlertTriangle size={12} color="var(--mantine-color-yellow-6)" />
                </Tooltip>
              )}
            </Group>
          ))}
          {proposal.extractedFields.length > 5 && (
            <Text size="xs" c="dimmed">+{proposal.extractedFields.length - 5} more fields</Text>
          )}
        </Stack>
      )}

      <Group gap="xs">
        <Button
          size="xs"
          color={color}
          leftSection={<IconCheck size={14} />}
          onClick={() => onConfirm(proposal)}
        >
          Confirm
        </Button>
        <Button
          size="xs"
          variant="light"
          leftSection={<IconEdit size={14} />}
          onClick={() => onEdit(proposal)}
        >
          Edit
        </Button>
        <Button
          size="xs"
          variant="subtle"
          color="gray"
          leftSection={<IconPlayerSkipForward size={14} />}
          onClick={() => onSkip(proposal)}
        >
          Skip
        </Button>
      </Group>
    </Card>
  );
}

/** Renders a completed session step */
function StepBadge({ step }: { step: IntakeStep }) {
  const label = step.name
    ? `${step.action.replace(/_/g, ' ')}: ${step.name}`
    : step.action.replace(/_/g, ' ');

  return (
    <Badge
      size="sm"
      variant="dot"
      color={step.action.includes('created') || step.action.includes('launched') ? 'green' : 'blue'}
    >
      {label}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function AgentAssistPanel({
  opened,
  onClose,
  onEntityCreated,
  onWorkflowLaunched,
}: AgentAssistPanelProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form modal state for "Edit" flow
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editProposal, setEditProposal] = useState<IntakeProposal | null>(null);

  const {
    process: agentProcess,
    proposals,
    response: agentResponse,
    suggestedNextSteps,
    steps,
    loading: processing,
    error,
    recordStep,
    resetSession,
  } = useIntakeProcess();

  const {
    processImage,
    scanning,
    ocrAvailable,
    statusMessage: scanStatus,
  } = useScanToFill();

  // Primitive mutations — resolved dynamically from proposal target
  const actorMutations = usePrimitiveMutations('actor');
  const tenantId = useTenantId() ?? '';
  const navigate = useNavigate();

  // DRR-182: Workflow launch via draft pattern
  const [createDraft] = useMutation<{
    createDraftInstance: { draftId?: string; existingId?: string; created: boolean; message?: string };
  }>(CREATE_DRAFT_INSTANCE);
  const [commitDraft] = useMutation<{
    commitDraftInstance: { id: string; status: string; currentState: string; instanceName?: string };
  }>(COMMIT_DRAFT_INSTANCE);
  const [startInstance] = useStartInstanceMutation();

  // ── Camera / Document Capture ───────────────────────────────────────────

  const handleCameraCapture = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleImageSelected = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // Step 1: On-device OCR (T4 stays on device)
      const scanResult = await processImage(file);

      if (!scanResult) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'system',
            content: 'Could not extract text from this image. Try a clearer photo.',
            timestamp: new Date(),
          },
        ]);
        return;
      }

      const ocrText = Object.values(scanResult).join(' ');

      setMessages((prev) => [
        ...prev,
        {
          role: 'user',
          content: `[Document captured — ${Object.keys(scanResult).length} fields extracted]`,
          timestamp: new Date(),
        },
      ]);

      recordStep({
        action: 'photo_captured',
        type: 'document',
        timestamp: new Date().toISOString(),
      });

      // Step 2: Send to agent (text is pseudonymized by hub before reaching Claude)
      const resultProposals = await agentProcess({
        prompt: 'Classify this document and extract entity fields for form pre-fill.',
        mediaText: ocrText,
      });

      if (agentResponse) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: agentResponse,
            timestamp: new Date(),
            proposals: resultProposals,
            suggestedNextSteps,
          },
        ]);
      }

      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [processImage, agentProcess, agentResponse, suggestedNextSteps, recordStep],
  );

  // ── Text Chat ───────────────────────────────────────────────────────────

  const handleSendMessage = useCallback(async () => {
    if (!inputText.trim()) return;
    const userMessage = inputText.trim();
    setInputText('');

    setMessages((prev) => [
      ...prev,
      { role: 'user', content: userMessage, timestamp: new Date() },
    ]);

    const resultProposals = await agentProcess({ prompt: userMessage });

    if (agentResponse) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: agentResponse,
          timestamp: new Date(),
          proposals: resultProposals,
          suggestedNextSteps,
        },
      ]);
    }
  }, [inputText, agentProcess, agentResponse, suggestedNextSteps]);

  // ── Proposal Actions ────────────────────────────────────────────────────

  /** Convert proposal extracted fields to mutation input */
  const proposalToInput = useCallback((proposal: IntakeProposal) => {
    const input: Record<string, unknown> = {};
    for (const field of proposal.extractedFields) {
      // Only include fields with sufficient confidence
      if (field.confidence >= 0.4) {
        input[field.fieldName] = field.value;
      }
    }
    return input;
  }, []);

  /** Confirm: create entity directly from proposal fields */
  const handleConfirmProposal = useCallback(
    async (proposal: IntakeProposal) => {
      const input = proposalToInput(proposal);
      const entityName = (input.name as string) ?? proposal.target;

      try {
        if (proposal.proposalType === 'create_actor') {
          const result = await actorMutations.create({
            name: entityName,
            ...input,
          });
          const createdId = result?.createActor?.id ?? `actor_${Date.now()}`;
          recordStep({
            action: 'actor_created',
            type: 'actor',
            entityId: createdId,
            name: entityName,
            timestamp: new Date().toISOString(),
          });
          onEntityCreated?.('actor', createdId);

          setMessages((prev) => [
            ...prev,
            { role: 'system', content: `Actor "${entityName}" created (${createdId}).`, timestamp: new Date() },
          ]);
        } else if (proposal.proposalType === 'launch_workflow') {
          // DRR-182: Draft → Commit → Start pattern
          const subjectActorId = steps.find((s) => s.action === 'actor_created')?.entityId;
          const workflowId = proposal.target;

          // Step 1: Create draft (Valkey-only)
          const draftResult = await createDraft({
            variables: {
              input: {
                workflowDefinitionId: workflowId,
                subjectActorId: subjectActorId ?? undefined,
                metadata: {},
              },
            },
          });

          const draft = draftResult.data?.createDraftInstance;
          if (!draft?.created || !draft.draftId) {
            const msg = draft?.existingId
              ? `An active instance already exists (${draft.existingId}).`
              : (draft?.message ?? 'Draft creation failed.');
            setMessages((prev) => [
              ...prev,
              { role: 'system', content: msg, timestamp: new Date() },
            ]);
            return;
          }

          // Step 2: Commit draft (Valkey → DDB)
          const commitResult = await commitDraft({
            variables: { draftId: draft.draftId },
          });

          const committed = commitResult.data?.commitDraftInstance;
          if (!committed?.id) {
            setMessages((prev) => [
              ...prev,
              { role: 'system', content: 'Failed to commit workflow instance.', timestamp: new Date() },
            ]);
            return;
          }

          // Step 3: Auto-start
          await startInstance({
            variables: {
              tenantId,
              instanceId: committed.id,
              workflowDefinitionId: workflowId,
            },
          });

          recordStep({
            action: 'workflow_launched',
            type: workflowId,
            entityId: committed.id,
            name: committed.instanceName ?? workflowId,
            timestamp: new Date().toISOString(),
          });
          onWorkflowLaunched?.(workflowId, committed.id);

          setMessages((prev) => [
            ...prev,
            {
              role: 'system',
              content: `Workflow "${committed.instanceName ?? workflowId}" launched (${committed.id}).`,
              timestamp: new Date(),
            },
          ]);

          // Navigate to the new instance
          navigate(`/instances/${committed.id}`);
        } else {
          // Generic primitive creation for other proposal types
          recordStep({
            action: `${proposal.target}_created`,
            type: proposal.target,
            entityId: `pending_${Date.now()}`,
            name: entityName,
            timestamp: new Date().toISOString(),
          });
          onEntityCreated?.(proposal.target, `pending_${Date.now()}`);
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Entity creation failed';
        setMessages((prev) => [
          ...prev,
          { role: 'system', content: `Error: ${msg}`, timestamp: new Date() },
        ]);
      }
    },
    [proposalToInput, actorMutations, createDraft, commitDraft, startInstance, tenantId, steps, recordStep, onEntityCreated, onWorkflowLaunched, navigate],
  );

  /** Edit: open PrimitiveFormModal with pre-filled values */
  const handleEditProposal = useCallback((proposal: IntakeProposal) => {
    setEditProposal(proposal);
    setEditModalOpen(true);
  }, []);

  /** Called when form modal submits successfully */
  const handleFormSubmit = useCallback(
    async (values: Record<string, unknown>) => {
      if (!editProposal) return;

      const entityName = (values.name as string) ?? editProposal.target;

      try {
        if (editProposal.proposalType === 'create_actor') {
          const result = await actorMutations.create({
            name: entityName,
            ...values,
          });
          const createdId = result?.createActor?.id ?? `actor_${Date.now()}`;
          recordStep({
            action: 'actor_created',
            type: 'actor',
            entityId: createdId,
            name: entityName,
            timestamp: new Date().toISOString(),
          });
          onEntityCreated?.('actor', createdId);
          setMessages((prev) => [
            ...prev,
            { role: 'system', content: `Actor "${entityName}" created (${createdId}).`, timestamp: new Date() },
          ]);
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Entity creation failed';
        setMessages((prev) => [
          ...prev,
          { role: 'system', content: `Error: ${msg}`, timestamp: new Date() },
        ]);
      }

      setEditModalOpen(false);
      setEditProposal(null);
    },
    [editProposal, actorMutations, recordStep, onEntityCreated],
  );

  const handleSkipProposal = useCallback((_proposal: IntakeProposal) => {
    setMessages((prev) => [
      ...prev,
      { role: 'system', content: 'Skipped. What would you like to do next?', timestamp: new Date() },
    ]);
  }, []);

  const handleNewSession = useCallback(() => {
    setMessages([]);
    setInputText('');
    resetSession();
  }, [resetSession]);

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconMessageChatbot size={20} />
          <Title order={4}>Agent Assist</Title>
        </Group>
      }
      position="right"
      size="lg"
      padding="md"
    >
      <Stack h="calc(100vh - 120px)" gap="md">
        {/* Session Progress */}
        {steps.length > 0 && (
          <Box>
            <Group gap="xs" mb={4}>
              <Text size="xs" fw={600}>Session</Text>
              <Text size="xs" c="dimmed">({steps.length} steps)</Text>
            </Group>
            <Group gap={4}>
              {steps.map((step, i) => (
                <StepBadge key={i} step={step} />
              ))}
            </Group>
          </Box>
        )}

        <Divider />

        {/* Chat Area */}
        <ScrollArea flex={1} ref={scrollAreaRef} offsetScrollbars>
          <Stack gap="sm">
            {messages.length === 0 && (
              <Alert variant="light" color="blue" title="Agent Assist">
                <Text size="sm">
                  Capture a document photo or describe what you need.
                  The agent will classify it, extract data, and guide you through entity creation.
                </Text>
                <Text size="xs" c="dimmed" mt="xs">
                  On-device OCR — documents processed locally.
                </Text>
              </Alert>
            )}

            {messages.map((msg, i) => (
              <Box
                key={i}
                style={{
                  alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '85%',
                }}
              >
                <Card
                  withBorder={msg.role !== 'system'}
                  shadow={msg.role === 'assistant' ? 'xs' : undefined}
                  p="xs"
                  radius="md"
                  bg={msg.role === 'user' ? 'blue.0' : msg.role === 'system' ? 'gray.0' : undefined}
                >
                  <Text size="sm">{msg.content}</Text>
                </Card>

                {msg.proposals && msg.proposals.length > 0 && (
                  <Stack gap="xs" mt="xs">
                    {msg.proposals.map((proposal, pi) => (
                      <ProposalCard
                        key={pi}
                        proposal={proposal}
                        onConfirm={handleConfirmProposal}
                        onEdit={handleEditProposal}
                        onSkip={handleSkipProposal}
                      />
                    ))}
                  </Stack>
                )}

                {msg.suggestedNextSteps && msg.suggestedNextSteps.length > 0 && (
                  <Group gap="xs" mt="xs">
                    {msg.suggestedNextSteps.map((step, si) => (
                      <Button
                        key={si}
                        size="xs"
                        variant="light"
                        onClick={() => setInputText(step)}
                      >
                        {step}
                      </Button>
                    ))}
                  </Group>
                )}
              </Box>
            ))}

            {(processing || scanning) && (
              <Group gap="xs">
                <Loader size="xs" />
                <Text size="xs" c="dimmed">
                  {scanning ? (scanStatus ?? 'Scanning...') : 'Processing...'}
                </Text>
              </Group>
            )}

            {error && (
              <Alert color="red" variant="light" title="Error">
                <Text size="sm">{error}</Text>
              </Alert>
            )}
          </Stack>
        </ScrollArea>

        <Divider />

        {/* Input */}
        <Box>
          <Group gap="xs" align="flex-end">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={handleImageSelected}
            />
            <Tooltip label={ocrAvailable ? 'Capture document (on-device OCR)' : 'Capture document'}>
              <ActionIcon
                size="lg"
                variant="light"
                color="teal"
                onClick={handleCameraCapture}
                disabled={processing || scanning}
              >
                {scanning ? <Loader size="xs" /> : <IconCamera size={20} />}
              </ActionIcon>
            </Tooltip>
            <Textarea
              placeholder="Ask the agent or describe what you need..."
              value={inputText}
              onChange={(e) => setInputText(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              autosize
              minRows={1}
              maxRows={3}
              style={{ flex: 1 }}
              disabled={processing}
            />
            <ActionIcon
              size="lg"
              variant="filled"
              color="blue"
              onClick={handleSendMessage}
              disabled={!inputText.trim() || processing}
            >
              <IconSend size={20} />
            </ActionIcon>
          </Group>
          <Group justify="space-between" mt="xs">
            <Text size="xs" c="dimmed">
              {ocrAvailable ? 'Device OCR active' : 'Server OCR fallback'}
            </Text>
            <Button size="xs" variant="subtle" color="gray" onClick={handleNewSession}>
              New Session
            </Button>
          </Group>
        </Box>
      </Stack>

      {/* Form modal for Edit flow — backend-driven fields via FormSchema (DRR-055) */}
      {editProposal && (
        <PrimitiveFormModal
          opened={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setEditProposal(null);
          }}
          primitiveType={editProposal.target as PrimitiveType}
          mode="create"
          initialData={{ id: '', name: '', ...proposalToInput(editProposal) }}
          onSubmit={handleFormSubmit}
        />
      )}
    </Drawer>
  );
}
